import { useEffect, useState } from "react";

import { CalendarFilledIcon } from "@vericus/cadmus-icons";
import {
  Button,
  Color,
  Combobox,
  DatePicker,
  SelectButton,
  Text,
} from "@vericus/cadmus-ui";
import { type DatesRangeValue } from "@vericus/cadmus-ui";

import { format } from "date-fns";

import { getLocalDate } from "@/utils/datetime";

import * as styles from "./date-range-filter.css";

export const dayHighlightKey = (d: Date) => format(d, "yyyy-MM-dd");

interface DateRangeFilterProps {
  label: string;
  maxDate: Date;
  selectedDateRange: DatesRangeValue;
  onDateRangeChange: (dateRange: DatesRangeValue) => void;

  open: boolean;
  setOpen: (open: boolean) => void;
}

export function DateRangeFilter({
  open,
  setOpen,
  label,
  maxDate,
  selectedDateRange,
  onDateRangeChange,
}: DateRangeFilterProps) {
  const todayKey = dayHighlightKey(new Date());
  const highlightDays: { [key: string]: Color[] } = {};
  highlightDays[todayKey] = ["cyan500"];

  const [value, setValue] = useState<DatesRangeValue>([
    selectedDateRange[0],
    selectedDateRange[1],
  ]);

  const getDisplayText = () => {
    if (selectedDateRange[0] && selectedDateRange[1]) {
      return `${format(new Date(selectedDateRange[0]), "MMM d")} - ${format(new Date(selectedDateRange[1]), "MMM d")}`;
    } else if (selectedDateRange[0]) {
      return `From ${format(new Date(selectedDateRange[0]), "MMM d")}`;
    } else if (selectedDateRange[1]) {
      return `Until ${format(new Date(selectedDateRange[1]), "MMM d")}`;
    }
    return label;
  };

  const hasSelectedRange =
    selectedDateRange[0] !== null || selectedDateRange[1] !== null;

  useEffect(() => {
    setValue([selectedDateRange[0], selectedDateRange[1]]);
  }, [selectedDateRange]);

  return (
    <Combobox.Root open={open} onOpenChange={(open: boolean) => setOpen(open)}>
      <Combobox.Trigger asChild>
        <SelectButton
          icon={<CalendarFilledIcon label="daterangepicker" size={24} />}
        >
          {getDisplayText()}
        </SelectButton>
      </Combobox.Trigger>
      <Combobox.Content align="start" style={{ width: "100%" }}>
        <div className={styles.datePickerContainer}>
          <Text kind="headingSix">{label}</Text>
          <DateDropdown
            value={value}
            setValue={setValue}
            maxDate={maxDate ? new Date(maxDate) : undefined}
          />

          {hasSelectedRange ? (
            <Button
              kind="outline"
              style={{ width: "80px" }}
              onClick={() => onDateRangeChange([null, null])}
            >
              Clear
            </Button>
          ) : (
            <Button
              kind="primary"
              style={{ width: "80px" }}
              onClick={() => onDateRangeChange(value)}
            >
              Apply
            </Button>
          )}
        </div>
      </Combobox.Content>
    </Combobox.Root>
  );
}

function DateDropdown({
  value,
  maxDate,
  setValue,
}: {
  value: DatesRangeValue;
  maxDate: Date | undefined;
  setValue: (value: DatesRangeValue) => void;
}) {
  const fromValue = value[0]
    ? format(new Date(value[0]), "MMM d, yyyy")
    : "From";
  const toValue = value[1] ? format(new Date(value[1]), "MMM d, yyyy") : "To";

  return (
    <Combobox.Root>
      <Combobox.Trigger asChild>
        <div className={styles.selectionContainer}>
          <SelectButton
            icon={<CalendarFilledIcon label={"from-date"} size={24} />}
          >
            {fromValue}
          </SelectButton>
          <SelectButton
            icon={<CalendarFilledIcon label={"from-date"} size={24} />}
          >
            {toValue}
          </SelectButton>
        </div>
      </Combobox.Trigger>
      <Combobox.Content align="center" style={{ width: "100%", padding: 10 }}>
        <DatePicker
          type="range"
          weekendDays={[]}
          withCellSpacing={false}
          maxLevel="month"
          value={value}
          onChange={([from, to]) => {
            const newFrom = from ? getLocalDate(from) : null;
            const newTo = to ? getLocalDate(to) : null;
            setValue([newFrom, newTo]);
          }}
          minDate={new Date()}
          maxDate={maxDate}
          numberOfColumns={1}
        />
      </Combobox.Content>
    </Combobox.Root>
  );
}
